import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, downloadBlob } from '@/lib/api';
import { 
  Search, 
  Eye, 
  Edit,
  Plus,
  Loader2,
  Store,
  Phone,
  MapPin,
  IndianRupee,
  Filter,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '@/components/ui/pagination';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const CUSTOMER_NAME_MAX_LENGTH = 20;
const CUSTOMER_ADDRESS_LINE_MAX_LENGTH = 25;

const normalizeAddressLines = (customer = {}) => ({
  address_line1: (customer.address_line1 || '').trim(),
  address_line2: (customer.address_line2 || '').trim(),
});

const normalizeCustomerType = (value) => {
  const cleaned = (value || '').toString().trim().toLowerCase().replace(/\s+/g, '_');
  if (['end_customer', 'endcustomer', 'consumer', 'customer'].includes(cleaned)) return 'end_customer';
  if (['retail_shop', 'retail', 'shop', 'retail_store', 'local_store'].includes(cleaned)) return 'retail_shop';
  if (['wholesale', 'wholesale_customer', 'bulk'].includes(cleaned)) return 'wholesale';
  return 'retail_shop';
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [outstandingFilter, setOutstandingFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [areas, setAreas] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pricingEdits, setPricingEdits] = useState({});
  const [pricingSaving, setPricingSaving] = useState(false);
  const [customerTypeEdit, setCustomerTypeEdit] = useState('retail_shop');
  const [typeSaving, setTypeSaving] = useState(false);
  const [addressLine1Edit, setAddressLine1Edit] = useState('');
  const [addressLine2Edit, setAddressLine2Edit] = useState('');
  const [areaEdit, setAreaEdit] = useState('');
  const [addressSaving, setAddressSaving] = useState(false);
  const [bosSettingSaving, setBosSettingSaving] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    shop_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    area: '',
    sales_staff_id: '',
    credit_allowed: false,
    show_prev_balance_on_bos: false,
    customer_type: 'retail_shop'
  });
  const [customerSaving, setCustomerSaving] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const { admin } = useAuth();
  const canEditPricing = admin?.role === 'admin';

  const resetCustomerForm = () => {
    setCustomerForm({
      name: '',
      shop_name: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      area: '',
      sales_staff_id: '',
      credit_allowed: false,
      show_prev_balance_on_bos: false,
      customer_type: 'retail_shop'
    });
    setEditingCustomer(null);
  };

  useEffect(() => {
    setPage(1);
  }, [search, areaFilter, staffFilter, outstandingFilter]);

  useEffect(() => {
    fetchData();
  }, [search, areaFilter, staffFilter, outstandingFilter, page]);

  const fetchData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (areaFilter !== 'all') params.area = areaFilter;
      if (staffFilter !== 'all') params.sales_staff_id = staffFilter;
      if (outstandingFilter !== 'all') params.has_outstanding = outstandingFilter === 'yes';
      
      params.page = page;
      params.page_size = pageSize;

      const [customersRes, staffRes, areasRes] = await Promise.all([
        adminApi.getCustomers(params),
        adminApi.getSalesStaff(),
        adminApi.getAreas()
      ]);
      
      setCustomers(customersRes.data.items || []);
      setTotal(customersRes.data.total || 0);
      setStaff(staffRes.data);
      setAreas(areasRes.data.areas || []);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const res = await adminApi.downloadAdminDatasetExcel('customers', {
        search: search || undefined,
        area: areaFilter !== 'all' ? areaFilter : undefined,
        staff_id: staffFilter !== 'all' ? staffFilter : undefined,
        has_outstanding: outstandingFilter === 'all' ? undefined : outstandingFilter === 'yes',
      });
      downloadBlob(res.data, 'customers.xlsx');
    } catch (error) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleViewDetails = async (customer) => {
    setDetailLoading(true);
    setShowDetailModal(true);
    try {
      const response = await adminApi.getCustomerById(customer.id);
      setSelectedCustomer(response.data);
      setCustomerTypeEdit(normalizeCustomerType(response.data?.customer_type));
      const normalizedAddress = normalizeAddressLines(response.data);
      setAddressLine1Edit(normalizedAddress.address_line1);
      setAddressLine2Edit(normalizedAddress.address_line2);
      setAreaEdit(response.data?.area || '');
      initializePricingEdits(response.data?.pricing || []);
    } catch (error) {
      toast.error('Failed to fetch customer details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!selectedCustomer) return;
    const nextAddressLine1 = addressLine1Edit.trim();
    const nextAddressLine2 = addressLine2Edit.trim();
    const nextArea = areaEdit.trim();
    if (!nextAddressLine1 || !nextArea) {
      toast.error('Address line 1 and area are required');
      return;
    }
    if (nextAddressLine1.length > CUSTOMER_ADDRESS_LINE_MAX_LENGTH || nextAddressLine2.length > CUSTOMER_ADDRESS_LINE_MAX_LENGTH) {
      toast.error(`Each address line must be ${CUSTOMER_ADDRESS_LINE_MAX_LENGTH} characters or less`);
      return;
    }

    setAddressSaving(true);
    try {
      const response = await adminApi.updateCustomer(selectedCustomer.id, {
        address_line1: nextAddressLine1,
        address_line2: nextAddressLine2,
        area: nextArea,
      });
      setSelectedCustomer(response.data);
      setCustomers((prev) => prev.map((c) => (c.id === selectedCustomer.id ? response.data : c)));
      toast.success('Customer address updated');
    } catch (error) {
      toast.error('Failed to update address');
    } finally {
      setAddressSaving(false);
    }
  };

  const openAddCustomer = () => {
    resetCustomerForm();
    setShowCustomerModal(true);
  };

  const openEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name || '',
      shop_name: customer.shop_name || '',
      phone: customer.phone || '',
      ...normalizeAddressLines(customer),
      area: customer.area || '',
      sales_staff_id: customer.sales_staff_id || '',
      credit_allowed: customer.credit_allowed || false,
      show_prev_balance_on_bos: customer.show_prev_balance_on_bos || false,
      customer_type: normalizeCustomerType(customer.customer_type)
    });
    setShowCustomerModal(true);
  };

  const handleSaveCustomer = async () => {
    if (!customerForm.name.trim() || !customerForm.shop_name.trim() || !customerForm.phone.trim()) {
      toast.error('Name, shop name, and phone are required');
      return;
    }
    if (customerForm.name.trim().length > CUSTOMER_NAME_MAX_LENGTH) {
      toast.error(`Customer name must be ${CUSTOMER_NAME_MAX_LENGTH} characters or less`);
      return;
    }
    if (!customerForm.address_line1.trim()) {
      toast.error('Address line 1 is required');
      return;
    }
    if (
      customerForm.address_line1.trim().length > CUSTOMER_ADDRESS_LINE_MAX_LENGTH ||
      customerForm.address_line2.trim().length > CUSTOMER_ADDRESS_LINE_MAX_LENGTH
    ) {
      toast.error(`Each address line must be ${CUSTOMER_ADDRESS_LINE_MAX_LENGTH} characters or less`);
      return;
    }
    if (!customerForm.area) {
      toast.error('Please select area');
      return;
    }
    if (!customerForm.sales_staff_id) {
      toast.error('Please select a sales staff');
      return;
    }

    setCustomerSaving(true);
    try {
      const payload = {
        name: customerForm.name.trim(),
        shop_name: customerForm.shop_name.trim(),
        phone: customerForm.phone.trim(),
        address_line1: customerForm.address_line1.trim(),
        address_line2: customerForm.address_line2.trim(),
        area: customerForm.area,
        sales_staff_id: customerForm.sales_staff_id,
        credit_allowed: customerForm.credit_allowed,
        show_prev_balance_on_bos: customerForm.show_prev_balance_on_bos,
        customer_type: customerForm.customer_type
      };

      if (editingCustomer) {
        await adminApi.updateCustomer(editingCustomer.id, payload);
        toast.success('Customer updated');
      } else {
        await adminApi.createCustomer(payload);
        toast.success('Customer created');
      }

      setShowCustomerModal(false);
      resetCustomerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleSaveCustomerType = async () => {
    if (!selectedCustomer) return;
    setTypeSaving(true);
    try {
      await adminApi.updateCustomer(selectedCustomer.id, {
        customer_type: customerTypeEdit,
      });
      const refreshed = await adminApi.getCustomerById(selectedCustomer.id);
      setSelectedCustomer(refreshed.data);
      setCustomerTypeEdit(normalizeCustomerType(refreshed.data?.customer_type));
      toast.success('Customer type updated');
    } catch (error) {
      toast.error('Failed to update customer type');
    } finally {
      setTypeSaving(false);
    }
  };

  const handleSaveBosVisibility = async () => {
    if (!selectedCustomer) return;
    setBosSettingSaving(true);
    try {
      const response = await adminApi.updateCustomer(selectedCustomer.id, {
        show_prev_balance_on_bos: !!selectedCustomer.show_prev_balance_on_bos,
      });
      setSelectedCustomer((prev) => ({ ...prev, ...response.data }));
      setCustomers((prev) => prev.map((c) => (c.id === selectedCustomer.id ? { ...c, ...response.data } : c)));
      toast.success('BOS previous balance setting updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update BOS setting');
    } finally {
      setBosSettingSaving(false);
    }
  };

  const initializePricingEdits = (pricing) => {
    const edits = {};
    pricing.forEach((variant) => {
      edits[variant.variant_id] = variant.custom_price ?? variant.default_price ?? '';
    });
    setPricingEdits(edits);
  };

  const handlePriceChange = (variantId, value) => {
    setPricingEdits((prev) => ({
      ...prev,
      [variantId]: value
    }));
  };

  const handleSavePricing = async () => {
    if (!selectedCustomer?.pricing) return;
    setPricingSaving(true);
    try {
      const payload = [];

      selectedCustomer.pricing.forEach((variant) => {
        const priceValue = pricingEdits[variant.variant_id];
        const parsed = parseFloat(priceValue);
        if (Number.isNaN(parsed)) return;
        const defaultPrice = variant.default_price;
        if (defaultPrice === undefined || parsed !== defaultPrice) {
          payload.push({
            variant_id: variant.variant_id,
            price: parsed
          });
        }
      });

      await adminApi.updateCustomerPricing(selectedCustomer.id, { pricing: payload });
      toast.success('Customer pricing updated');

      const refreshed = await adminApi.getCustomerById(selectedCustomer.id);
      setSelectedCustomer(refreshed.data);
      initializePricingEdits(refreshed.data?.pricing || []);
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleCreateOrderForCustomer = (customer) => {
    if (!customer?.id) return;
    navigate(`/orders?create_order=1&customer_id=${customer.id}`);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <LoadingOverlay open={downloadingExcel} message="Please wait. Report is being generated" />
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Customers</h1>
        <p className="text-[#A8A8A8] mt-1">View and manage all customers</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleDownloadExcel}
          className="border-[#E5E5E5]"
        >
          Download Excel
        </Button>
        <Button
          onClick={openAddCustomer}
          className="bg-[#C97A5E] hover:bg-[#B06850]"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A8A8]" />
              <Input
                placeholder="Search by name, business, or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="search-input"
              />
            </div>
            <Select value={areaFilter} onValueChange={setAreaFilter}>
              <SelectTrigger className="w-full lg:w-40 border-[#E5E5E5]" data-testid="area-filter">
                <SelectValue placeholder="Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {areas.map((area) => (
                  <SelectItem key={area} value={area}>{area}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={staffFilter} onValueChange={setStaffFilter}>
              <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]" data-testid="staff-filter">
                <SelectValue placeholder="Sales Staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={outstandingFilter} onValueChange={setOutstandingFilter}>
              <SelectTrigger className="w-full lg:w-48 border-[#E5E5E5]" data-testid="outstanding-filter">
                <SelectValue placeholder="Outstanding" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Has Outstanding</SelectItem>
                <SelectItem value="no">No Outstanding</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">
              {editingCustomer ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Name *</Label>
                <Input
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                  maxLength={CUSTOMER_NAME_MAX_LENGTH}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="customer-name-input"
                />
                <p className="text-xs text-[#A8A8A8] text-right">{customerForm.name.length}/{CUSTOMER_NAME_MAX_LENGTH}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Shop Name *</Label>
                <Input
                  value={customerForm.shop_name}
                  onChange={(e) => setCustomerForm({ ...customerForm, shop_name: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="customer-shop-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Phone *</Label>
                <Input
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                  className="border-[#E5E5E5] focus:border-[#C97A5E]"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Area</Label>
                <Select
                  value={customerForm.area}
                  onValueChange={(value) => setCustomerForm({ ...customerForm, area: value })}
                >
                  <SelectTrigger className="border-[#E5E5E5] focus:border-[#C97A5E]" data-testid="customer-area-input">
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                    {customerForm.area && !areas.includes(customerForm.area) && (
                      <SelectItem value={customerForm.area}>{customerForm.area}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Address Line 1 *</Label>
              <Input
                value={customerForm.address_line1}
                onChange={(e) => setCustomerForm({ ...customerForm, address_line1: e.target.value })}
                maxLength={CUSTOMER_ADDRESS_LINE_MAX_LENGTH}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="customer-address-line1-input"
              />
              <p className="text-xs text-[#A8A8A8] text-right">{customerForm.address_line1.length}/{CUSTOMER_ADDRESS_LINE_MAX_LENGTH}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Address Line 2</Label>
              <Input
                value={customerForm.address_line2}
                onChange={(e) => setCustomerForm({ ...customerForm, address_line2: e.target.value })}
                maxLength={CUSTOMER_ADDRESS_LINE_MAX_LENGTH}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
                data-testid="customer-address-line2-input"
              />
              <p className="text-xs text-[#A8A8A8] text-right">{customerForm.address_line2.length}/{CUSTOMER_ADDRESS_LINE_MAX_LENGTH}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Sales Staff *</Label>
                <Select
                  value={customerForm.sales_staff_id}
                  onValueChange={(value) => setCustomerForm({ ...customerForm, sales_staff_id: value })}
                >
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#5C4033]">Customer Type</Label>
                <Select
                  value={customerForm.customer_type}
                  onValueChange={(value) => setCustomerForm({ ...customerForm, customer_type: value })}
                >
                  <SelectTrigger className="border-[#E5E5E5]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="end_customer">End Customer</SelectItem>
                    <SelectItem value="retail_shop">Retail Shop</SelectItem>
                    <SelectItem value="wholesale">Wholesale Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#5C4033]">
              <input
                type="checkbox"
                checked={customerForm.credit_allowed}
                onChange={(e) => setCustomerForm({ ...customerForm, credit_allowed: e.target.checked })}
              />
              Credit Allowed
            </label>
            <label className="flex items-center gap-2 text-sm text-[#5C4033]">
              <input
                type="checkbox"
                checked={customerForm.show_prev_balance_on_bos}
                onChange={(e) => setCustomerForm({ ...customerForm, show_prev_balance_on_bos: e.target.checked })}
              />
              Show Previous Balance on BOS
            </label>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowCustomerModal(false); resetCustomerForm(); }}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCustomer}
                disabled={customerSaving}
                className="bg-[#C97A5E] hover:bg-[#B06850]"
                data-testid="save-customer-btn"
              >
                {customerSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingCustomer ? 'Save Changes' : 'Create Customer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customers Table */}
      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#A8A8A8]">
              <Store className="w-12 h-12 mb-4" />
              <p>No customers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Customer</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Phone</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Area</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Sales Staff</th>
                    <th className="text-left py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Outstanding</th>
                    <th className="text-right py-4 px-6 text-xs font-semibold text-[#5C4033] uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {customers.map((c) => (
                    <tr key={c.id} className="hover:bg-[#FAFAF9]/50 transition-colors" data-testid={`customer-row-${c.id}`}>
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-[#2D2420]">{c.name}</p>
                          <p className="text-sm text-[#A8A8A8]">{c.shop_name}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-[#2D2420]">{c.phone}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{c.area}</td>
                      <td className="py-4 px-6 text-[#2D2420]">{c.sales_staff_name}</td>
                      <td className="py-4 px-6">
                        <span className={cn(
                          "font-semibold",
                          c.outstanding_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                        )}>
                          {formatCurrency(c.outstanding_amount)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCreateOrderForCustomer(c)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`create-order-customer-${c.id}`}
                          >
                            Create Order
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditCustomer(c)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`edit-customer-${c.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(c)}
                            className="text-[#5C4033] hover:bg-[#FAFAF9]"
                            data-testid={`view-customer-${c.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
        {!loading && total > 0 && (
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-[#E5E5E5]">
            <p className="text-sm text-[#A8A8A8]">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.max(1, prev - 1));
                    }}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-[#5C4033]">
                    Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((prev) => Math.min(Math.max(1, Math.ceil(total / pageSize)), prev + 1));
                    }}
                    className={page >= Math.max(1, Math.ceil(total / pageSize)) ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Customer Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : selectedCustomer && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-xl bg-[#C97A5E]/10 flex items-center justify-center text-[#C97A5E] text-2xl font-bold">
                  {selectedCustomer.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#2D2420]">{selectedCustomer.name}</h3>
                  <p className="text-[#A8A8A8]">{selectedCustomer.shop_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#5C4033]">
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" /> {selectedCustomer.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {selectedCustomer.area}
                    </span>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Total Orders</p>
                  <p className="text-lg font-bold text-[#2D2420]">{formatCurrency(selectedCustomer.total_orders_amount)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Total Paid</p>
                  <p className="text-lg font-bold text-[#4A7c59]">{formatCurrency(selectedCustomer.total_payments)}</p>
                </div>
                <div className="p-4 bg-[#FAFAF9] rounded-xl text-center">
                  <p className="text-xs text-[#A8A8A8]">Outstanding</p>
                  <p className={cn(
                    "text-lg font-bold",
                    selectedCustomer.outstanding_amount > 0 ? "text-[#E76F51]" : "text-[#4A7c59]"
                  )}>
                    {formatCurrency(selectedCustomer.outstanding_amount)}
                  </p>
                </div>
              </div>

              {/* Address Details */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-[#2D2420]">Address Details</h4>
                  <Button
                    size="sm"
                    onClick={handleSaveAddress}
                    disabled={addressSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {addressSaving ? 'Saving...' : 'Save Address'}
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[#A8A8A8]">Address Line 1</label>
                    <Input
                      value={addressLine1Edit}
                      onChange={(e) => setAddressLine1Edit(e.target.value)}
                      placeholder="Enter address line 1"
                      maxLength={CUSTOMER_ADDRESS_LINE_MAX_LENGTH}
                      className="mt-1 border-[#E5E5E5]"
                    />
                    <p className="text-[11px] text-[#A8A8A8] text-right mt-1">{addressLine1Edit.length}/{CUSTOMER_ADDRESS_LINE_MAX_LENGTH}</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#A8A8A8]">Address Line 2</label>
                    <Input
                      value={addressLine2Edit}
                      onChange={(e) => setAddressLine2Edit(e.target.value)}
                      placeholder="Enter address line 2"
                      maxLength={CUSTOMER_ADDRESS_LINE_MAX_LENGTH}
                      className="mt-1 border-[#E5E5E5]"
                    />
                    <p className="text-[11px] text-[#A8A8A8] text-right mt-1">{addressLine2Edit.length}/{CUSTOMER_ADDRESS_LINE_MAX_LENGTH}</p>
                  </div>
                  <div>
                    <label className="text-xs text-[#A8A8A8]">Area</label>
                    <Select value={areaEdit} onValueChange={setAreaEdit}>
                      <SelectTrigger className="mt-1 border-[#E5E5E5]">
                        <SelectValue placeholder="Select area" />
                      </SelectTrigger>
                      <SelectContent>
                        {areas.map((area) => (
                          <SelectItem key={area} value={area}>{area}</SelectItem>
                        ))}
                        {areaEdit && !areas.includes(areaEdit) && (
                          <SelectItem value={areaEdit}>{areaEdit}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Orders History */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#2D2420]">Customer Type</h4>
                  <Button
                    size="sm"
                    onClick={handleSaveCustomerType}
                    disabled={typeSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {typeSaving ? 'Saving...' : 'Save Type'}
                  </Button>
                </div>
                <Select value={customerTypeEdit} onValueChange={setCustomerTypeEdit}>
                  <SelectTrigger className="w-full border-[#E5E5E5]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="end_customer">End Customer</SelectItem>
                    <SelectItem value="retail_shop">Retail Shop</SelectItem>
                    <SelectItem value="wholesale">Wholesale Customer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A8A8A8] mt-2">
                  Pricing defaults will follow the customer type unless overridden.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#2D2420]">Bill of Supply</h4>
                  <Button
                    size="sm"
                    onClick={handleSaveBosVisibility}
                    disabled={bosSettingSaving}
                    className="bg-[#C97A5E] hover:bg-[#B06850]"
                  >
                    {bosSettingSaving ? 'Saving...' : 'Save BOS Setting'}
                  </Button>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#5C4033]">
                  <input
                    type="checkbox"
                    checked={!!selectedCustomer.show_prev_balance_on_bos}
                    onChange={(e) => setSelectedCustomer((prev) => ({ ...prev, show_prev_balance_on_bos: e.target.checked }))}
                  />
                  Show Previous Balance on default BOS
                </label>
                <p className="text-xs text-[#A8A8A8] mt-2">
                  Disabled by default. Internal BOS downloads can still include previous balance.
                </p>
              </div>

              {/* Orders History */}
              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Recent Orders</h4>
                {selectedCustomer.orders?.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No orders yet</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCustomer.orders?.map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#2D2420]">{order.order_id}</p>
                          <p className="text-xs text-[#A8A8A8]">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#2D2420]">{formatCurrency(order.total_amount)}</p>
                          <Badge className={cn(
                            "text-xs",
                            order.status === 'delivered' ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          )}>
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payments History */}
              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Recent Payments</h4>
                {selectedCustomer.payments?.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No payments yet</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCustomer.payments?.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-[#FAFAF9] rounded-lg">
                        <div>
                          <p className="font-medium text-[#2D2420]">{payment.payment_method}</p>
                          <p className="text-xs text-[#A8A8A8]">{formatDate(payment.created_at)}</p>
                        </div>
                        <p className="font-semibold text-[#4A7c59]">+{formatCurrency(payment.amount)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Credit Notes</h4>
                {selectedCustomer.credit_notes?.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No credit notes yet</p>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-y-auto">
                    {selectedCustomer.credit_notes?.map((note) => (
                      <div key={note.id} className="flex items-start justify-between gap-3 p-3 bg-[#FAFAF9] rounded-lg border border-[#EFEAE6]">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#2D2420]">{note.credit_note_no}</p>
                            <Badge className={cn(
                              'text-xs',
                              note.status === 'REDEEMED'
                                ? 'bg-green-100 text-green-800'
                                : note.status === 'POSTED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            )}>
                              {note.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#A8A8A8] mt-1">{formatDate(note.date || note.created_at)}</p>
                          {note.reason ? <p className="text-sm text-[#5C4033] mt-2">{note.reason}</p> : null}
                          {note.redeemed_at ? <p className="text-[11px] text-[#4A7c59] mt-1">Redeemed on {formatDate(note.redeemed_at)}</p> : null}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#2D2420]">{formatCurrency(note.amount || 0)}</p>
                          {note.credit_note_url ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="outline"
                              className="h-8 w-8 border-[#E5E5E5]"
                              onClick={() => window.open(note.credit_note_url, '_blank', 'noopener,noreferrer')}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Visit Outcomes */}
              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Visit Outcomes</h4>
                {selectedCustomer.visit_logs?.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No visit outcomes yet</p>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-y-auto">
                    {(selectedCustomer.visit_logs || []).map((visit) => (
                      <div key={visit.id} className="p-3 bg-[#FAFAF9] rounded-lg border border-[#EFEAE6]">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-[#2D2420]">{visit.sales_staff_name || 'Staff Visit'}</p>
                          <p className="text-xs text-[#A8A8A8]">
                            {visit.check_out_at ? formatDate(visit.check_out_at) : (visit.check_in_at ? formatDate(visit.check_in_at) : '-')}
                          </p>
                        </div>
                        <p className="text-sm text-[#5C4033] mt-2 whitespace-pre-wrap">
                          {visit.check_out_note || 'No outcome provided'}
                        </p>
                        {visit.duration_minutes ? (
                          <p className="text-[11px] text-[#A8A8A8] mt-1">Duration: {visit.duration_minutes} min</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer 360 Timeline */}
              <div>
                <h4 className="font-semibold text-[#2D2420] mb-3">Customer 360 Timeline</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className="p-3 bg-[#FAFAF9] rounded-lg text-center">
                    <p className="text-xs text-[#A8A8A8]">Leads</p>
                    <p className="font-bold text-[#2D2420]">{selectedCustomer.customer_360?.total_leads || 0}</p>
                  </div>
                  <div className="p-3 bg-[#FAFAF9] rounded-lg text-center">
                    <p className="text-xs text-[#A8A8A8]">Interactions</p>
                    <p className="font-bold text-[#2D2420]">{selectedCustomer.customer_360?.total_interactions || 0}</p>
                  </div>
                  <div className="p-3 bg-[#FAFAF9] rounded-lg text-center">
                    <p className="text-xs text-[#A8A8A8]">Orders</p>
                    <p className="font-bold text-[#2D2420]">{selectedCustomer.customer_360?.total_orders || 0}</p>
                  </div>
                  <div className="p-3 bg-[#FAFAF9] rounded-lg text-center">
                    <p className="text-xs text-[#A8A8A8]">Payments</p>
                    <p className="font-bold text-[#2D2420]">{selectedCustomer.customer_360?.total_payments || 0}</p>
                  </div>
                </div>

                {!(selectedCustomer.customer_360?.timeline || []).length ? (
                  <p className="text-center text-[#A8A8A8] py-4">No timeline entries</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {(selectedCustomer.customer_360?.timeline || []).slice(0, 20).map((event, idx) => (
                      <div key={`${event.type}-${event.ref}-${idx}`} className="p-3 bg-[#FAFAF9] rounded-lg border border-[#EFEAE6]">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-[#2D2420]">{event.title}</p>
                            {event.subtitle ? <p className="text-xs text-[#A8A8A8] mt-1">{event.subtitle}</p> : null}
                          </div>
                          <div className="text-right">
                            <Badge className="text-[10px] bg-[#F2ECE8] text-[#5C4033]">{event.type}</Badge>
                            {event.amount ? <p className="text-xs font-semibold text-[#2D2420] mt-1">{formatCurrency(event.amount)}</p> : null}
                          </div>
                        </div>
                        <p className="text-[11px] text-[#A8A8A8] mt-2">{event.created_at ? formatDate(event.created_at) : '-'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Customer Pricing */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-[#2D2420]">Customer Pricing</h4>
                  {canEditPricing && (
                    <Button
                      size="sm"
                      onClick={handleSavePricing}
                      disabled={pricingSaving}
                      className="bg-[#C97A5E] hover:bg-[#B06850]"
                      data-testid="save-customer-pricing"
                    >
                      {pricingSaving ? 'Saving...' : 'Save Pricing'}
                    </Button>
                  )}
                </div>

                {!selectedCustomer.pricing || selectedCustomer.pricing.length === 0 ? (
                  <p className="text-center text-[#A8A8A8] py-4">No pricing data available</p>
                ) : (
                  <div className="space-y-4">
                    {selectedCustomer.pricing.map((variant) => (
                      <div key={variant.variant_id} className="border border-[#E5E5E5] rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold text-[#2D2420]">{variant.master_name} · {variant.pack_size_label}</p>
                            <p className="text-xs text-[#A8A8A8]">SKU: {variant.sku}</p>
                          </div>
                          {variant.custom_price != null && (
                            <Badge className="text-[10px] bg-[#C97A5E]/10 text-[#C97A5E]">Custom</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <p className="text-xs text-[#A8A8A8]">End Customer (MRP)</p>
                            <p className="font-semibold text-[#2D2420]">{formatCurrency(variant.retail_mrp || 0)}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-[#A8A8A8]">Retail Shop</p>
                            <p className="font-semibold text-[#2D2420]">{formatCurrency(variant.retail_price || 0)}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-[#A8A8A8]">Wholesale</p>
                            <p className="font-semibold text-[#2D2420]">{formatCurrency(variant.wholesale_price || 0)}</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label className="text-xs text-[#A8A8A8]">Customer Price</label>
                          <div className="flex items-center gap-2 mt-1">
                            <IndianRupee className="w-3 h-3 text-[#A8A8A8]" />
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={pricingEdits?.[variant.variant_id] ?? ''}
                              onChange={(e) => handlePriceChange(
                                variant.variant_id,
                                e.target.value
                              )}
                              disabled={!canEditPricing}
                              className="border-[#E5E5E5] focus:border-[#C97A5E]"
                              data-testid={`price-${variant.variant_id}`}
                            />
                          </div>
                          <p className="text-[10px] text-[#A8A8A8] mt-1">
                            Default: {formatCurrency(variant.default_price || 0)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
